/**
 * FunnelRuntimeProvider
 *
 * Manages funnel session assignment and provides step evaluation
 * to page components. Generates a sessionId on first visit (stored
 * in sessionStorage) and calls the assign endpoint once per session.
 */

import { useState, useEffect, useCallback, type ReactNode } from 'react'
import {
  assignFunnel,
  evaluateStep,
  acceptStep as acceptStepApi,
  declineStep as declineStepApi,
} from './funnel-runtime.service'
import { FunnelRuntimeContext } from './funnel-runtime-context'
import type {
  FunnelEvaluateStepResponse,
  StepNavigationResponse,
  PageContext,
  AcceptParams,
} from './funnel-runtime.types'

const SESSION_ID_KEY = 'funnel_session_id'

function getOrCreateSessionId(): string {
  try {
    const existing = sessionStorage.getItem(SESSION_ID_KEY)
    if (existing) return existing
    const id = crypto.randomUUID()
    sessionStorage.setItem(SESSION_ID_KEY, id)
    return id
  } catch {
    return crypto.randomUUID()
  }
}

const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  US: 'USD', CA: 'CAD', GB: 'GBP', AU: 'AUD', NL: 'EUR',
  SE: 'SEK', DE: 'EUR', DK: 'DKK', FI: 'EUR', IE: 'EUR', FR: 'EUR',
}

export function FunnelRuntimeProvider({ children }: { children: ReactNode }) {
  const [sessionId] = useState(getOrCreateSessionId)
  const [assignedFunnelId, setAssignedFunnelId] = useState<string | null>(null)
  const [assignedFunnelName, setAssignedFunnelName] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let cancelled = false

    async function doAssign() {
      try {
        const locale = navigator.language || 'en-US'
        const parts = locale.split('-')
        const customerCountry = parts[1]?.toUpperCase()
        const customerCurrency = customerCountry
          ? COUNTRY_CURRENCY_MAP[customerCountry]
          : undefined

        const result = await assignFunnel({
          sessionId,
          urlPath: window.location.pathname,
          customerCurrency,
          customerCountry,
        })
        if (!cancelled && result.funnelId) {
          setAssignedFunnelId(result.funnelId)
          setAssignedFunnelName(result.funnelName)
        }
      } catch (err) {
        console.error('[FunnelRuntimeProvider] assign failed:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    doAssign()
    return () => { cancelled = true }
  }, [sessionId])

  const evaluatePageStep = useCallback(
    async (pageContext: PageContext): Promise<FunnelEvaluateStepResponse> => {
      if (!assignedFunnelId) {
        return { hasExperience: false, step: null }
      }
      return evaluateStep({
        sessionId,
        funnelId: assignedFunnelId,
        pageContext,
      })
    },
    [sessionId, assignedFunnelId],
  )

  const acceptOffer = useCallback(
    async (params: AcceptParams): Promise<StepNavigationResponse> => {
      if (!assignedFunnelId) {
        return { complete: true, nextStep: null }
      }
      return acceptStepApi({
        funnelId: assignedFunnelId,
        stepId: params.stepId,
        sessionId,
        action: params.action ?? 'add_to_cart',
        productId: params.productId,
        variantId: params.variantId,
        quantity: params.quantity,
      })
    },
    [sessionId, assignedFunnelId],
  )

  const declineOffer = useCallback(
    async (stepId: string): Promise<StepNavigationResponse> => {
      if (!assignedFunnelId) {
        return { complete: true, nextStep: null }
      }
      return declineStepApi({
        funnelId: assignedFunnelId,
        stepId,
        sessionId,
      })
    },
    [sessionId, assignedFunnelId],
  )

  return (
    <FunnelRuntimeContext.Provider
      value={{
        sessionId,
        assignedFunnelId,
        assignedFunnelName,
        loading,
        evaluatePageStep,
        acceptOffer,
        declineOffer,
      }}
    >
      {children}
    </FunnelRuntimeContext.Provider>
  )
}
